import { useI18n } from '@affine/i18n';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import { JourneyProgress } from '../journey-progress';
import * as styles from './auth-journey-shell.css';
import {
  AUTH_JOURNEY_MANIFEST,
  type AuthJourneyStageState,
  type AuthJourneyViewState,
} from './journey-manifest';
import { readAuthJourneyPresentationMarker } from './journey-presentation-state';
import { resolveAuthJourneyRoutePlan } from './journey-router';
import { useAuthJourneyTelemetry } from './journey-telemetry';

const statusKeyByState = {
  pending: null,
  active: 'com.streamlain.auth.journey.status.current',
  completed: 'com.streamlain.auth.journey.status.completed',
  'conditional-unresolved': 'com.streamlain.auth.journey.status.conditional',
  skipped: 'com.streamlain.auth.journey.status.skipped',
  blocked: 'com.streamlain.auth.journey.status.blocked',
  error: 'com.streamlain.auth.journey.status.error',
} as const satisfies Record<AuthJourneyStageState, string | null>;

const useResolvedAuthJourneyPlan = (viewState: AuthJourneyViewState) => {
  const rootJourneyId =
    typeof window === 'undefined'
      ? null
      : (readAuthJourneyPresentationMarker()?.journeyId ?? null);
  return useMemo(
    () => resolveAuthJourneyRoutePlan(viewState, rootJourneyId),
    [rootJourneyId, viewState]
  );
};

export const AuthJourneyOverview = ({
  viewState,
}: {
  viewState: AuthJourneyViewState;
}) => {
  const routePlan = useResolvedAuthJourneyPlan(viewState);
  useAuthJourneyTelemetry(routePlan);
  const t = useI18n();
  if (!routePlan.currentStageId) {
    return null;
  }
  const definition = AUTH_JOURNEY_MANIFEST[routePlan.journeyId];
  const stateById = new Map(routePlan.stages.map(stage => [stage.id, stage]));
  const visibleStages = definition.stages.filter(
    stage => stateById.get(stage.id)?.state !== 'skipped'
  );
  if (visibleStages.length <= 1) {
    return null;
  }

  return (
    <div data-testid="auth-journey-overview" data-journey-id={definition.id}>
      <JourneyProgress
        ariaLabel={t.t('com.streamlain.auth.journey.progress.label')}
        steps={visibleStages.map((stage, index) => {
          const view = stateById.get(stage.id);
          if (!view) {
            throw new Error(
              `Missing stage "${stage.id}" in journey "${definition.id}"`
            );
          }
          const isCurrent = routePlan.currentStageId === stage.id;
          const statusKey = statusKeyByState[view.state];
          const label = t.t(stage.labelKey);
          const status = statusKey ? t.t(statusKey) : view.state;
          const condition =
            view.state === 'conditional-unresolved' && stage.conditionLabelKey
              ? t.t(stage.conditionLabelKey)
              : null;
          return {
            ariaLabel: `${index + 1}/${visibleStages.length}: ${label} — ${status}${condition ? ` — ${condition}` : ''}`,
            dataState: view.state,
            id: stage.id,
            label,
            state: isCurrent
              ? ('current' as const)
              : view.state === 'completed' || view.state === 'skipped'
                ? ('completed' as const)
                : view.state === 'blocked' || view.state === 'error'
                  ? ('problem' as const)
                  : ('pending' as const),
            testId: `auth-journey-stage-${stage.id}`,
          };
        })}
      />
    </div>
  );
};

const AuthJourneyProgressContext = createContext<AuthJourneyViewState | null>(
  null
);

export const AuthJourneyProgress = () => {
  const viewState = useContext(AuthJourneyProgressContext);
  return viewState ? <AuthJourneyOverview viewState={viewState} /> : null;
};

export const AuthJourneyShell = ({
  viewState,
  children,
}: PropsWithChildren<{ viewState: AuthJourneyViewState }>) => {
  const routePlan = useResolvedAuthJourneyPlan(viewState);

  return (
    <AuthJourneyProgressContext.Provider value={routePlan}>
      <section
        className={styles.shell}
        data-testid="auth-journey-shell"
        data-journey-id={routePlan.journeyId}
      >
        <div className={styles.content}>{children}</div>
      </section>
    </AuthJourneyProgressContext.Provider>
  );
};
